// Copyright 2025, Imprivata, Inc.  All rights reserved.

import { SendOutlined } from '@ant-design/icons';
import { getUserDetails } from '@api/portalServices';
import { type ImprUser, isAdmin, type User } from '@api/types';
import { isCurrentUser, useSession } from '@common/authorization/context/Session';
import SelectUsers from '@components/selectUsers/SelectUsers';
import { useErrorContext } from '@errorHandler/context/ErrorContext';
import { type ApiError, AppError } from '@errorHandler/errors';
import { Button, Modal } from '@imprivata-cloud/components';
import { useDeleteIdentityUser, useIdentityDir } from '@imprivata-cloud/core';
import { Checkbox, Flex, message, Spin } from 'antd';
import type { Key } from 'antd/lib/table/interface';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import QuestionImg from '../../../assets/icons/circle-question-mark.svg?react';
import ImprUserList from '../users/impr-list-view/ImprUserList';
import IdentityUsersEmpty from './empty/IdentityUsersEmpty';
import useIdpInfiniteScroll from './hooks/useIdpInfiniteScroll';
import { useInviteUsers } from './hooks/useInviteUsers';
import { useUnlockUser } from './hooks/useUnlockUser';
import ImportUsersModal from './modals/importUsersModal/ImportUsersModal';
import UserModal from './modals/UserModal';

const TRANSLATION_ROOT = 'portal.settings.identity-users';
const TRANSLATION_REMOVE = 'common.remove';

const IdentityUsers = () => {
  const { t } = useTranslation();
  const { data: directories, error, refetch: refetchDirectories } = useIdentityDir();
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [isImportUsersModalOpen, setIsImportUsersModalOpen] = useState(false);
  const { emitError } = useErrorContext();
  const lastElementRef = useRef<HTMLDivElement | null>(null);
  const [editUserData, setEditUserData] = useState<ImprUser>();
  const { mutate: deleteUser } = useDeleteIdentityUser();
  const { mutate: unlockUserMutation, isPending: isUnlockUserPending } = useUnlockUser();
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);
  const { userInfo: sessionUserInfo } = useSession();
  const [unlockedUserIds, setUnlockedUserIds] = useState<Set<string>>(new Set());
  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);
  const { mutate: inviteUsersMutation, isPending: isInvitePending } = useInviteUsers();
  const [messageApi, contextHolder] = message.useMessage();

  const isPending = isUnlockUserPending || isInvitePending;

  const {
    data: users = [],
    isFetchingNextPage,
    refetch: refetchUsers,
    isLoading,
    error: errorUsers,
  } = useIdpInfiniteScroll({ lastElementRef });

  useEffect(() => {
    if (error || errorUsers) {
      console.error('Error fetching identity users: ', error);
      emitError(error as AppError);
    }
  }, [error, errorUsers, emitError]);

  const prevUsersCountRef = useRef(0);

  useEffect(() => {
    const prevCount = prevUsersCountRef.current;

    if (users.length < prevCount) {
      setSelectedRowKeys([]);
    } else if (users.length > prevCount && prevCount > 0) {
      setSelectedRowKeys(prev => {
        if (prev.length === prevCount) {
          const newKeys = users.slice(prevCount).map(u => u.userId as Key);
          return [...prev, ...newKeys];
        }
        return prev;
      });
    }
    prevUsersCountRef.current = users.length;
  }, [users]);

  const refetchUserList = useCallback(async () => {
    console.log('[IdentityUsers] refetchUserList');
    refetchUsers();
  }, [refetchUsers]);

  const unlockUser = useCallback(
    (userId: string, hasAccountLock: boolean, factorTypes: string[]) => {
      unlockUserMutation(
        { userId, hasAccountLock, factorTypes },
        {
          onSuccess: () => {
            console.log('User unlocked successfully:', userId);
            setUnlockedUserIds(prev => new Set(prev).add(userId));
          },
          onError: (e: unknown) => {
            console.error('Error unlocking user:', e);
            emitError(new AppError(e as ApiError));
          },
        },
      );
    },
    [unlockUserMutation, emitError],
  );

  const showUnlockUserButton = useCallback((user: ImprUser | User): boolean => {
    const userId = user.userId;
    if (!userId) {
      return false;
    }
    const isAccountLocked = user.lockedUntil
      ? new Date(user.lockedUntil).getTime() > Date.now()
      : false;
    const hasFactorLockouts =
      user.factorLockouts?.some(f => new Date(f.lockedUntil).getTime() > Date.now()) ?? false;
    return isAccountLocked || hasFactorLockouts;
  }, []);

  const editUser = async (userId: string) => {
    console.debug('edit user', userId);
    try {
      const userDetails = await getUserDetails({ userId: userId });
      const editUserInfo = userDetails.user;
      setEditUserData(editUserInfo);
      setIsUserModalOpen(true);
    } catch (e) {
      console.error('Error fetching user details:', e);
      emitError(e as AppError);
    }
  };

  const inviteUsers = useCallback(
    (ids: Key[]) => {
      if (!ids?.length) {
        return;
      }
      inviteUsersMutation(
        { userIds: ids as string[] },
        {
          onSuccess: response => {
            setSelectedRowKeys([]);
            const count = ids.length;
            const scheduledCount = response?.scheduledInvitationsCount || 0;
            const bannerParams = { duration: 10, className: 'invite-banner' };
            if (count === scheduledCount) {
              void messageApi.success({
                ...bannerParams,
                content: t(`${TRANSLATION_ROOT}.banners.invite-success`, { count }),
              });
            } else {
              void messageApi.warning({
                ...bannerParams,
                content: t(`${TRANSLATION_ROOT}.banners.invite-partial-success`, {
                  scheduledCount,
                  count,
                }),
              });
            }
          },
          onError: (e: unknown) => {
            console.error('Error inviting users: ', ids, e);
            emitError(new AppError(e as ApiError));
          },
        },
      );
    },
    [inviteUsersMutation, emitError, t, messageApi],
  );

  const selectUsersOptions = useMemo(
    () => [
      {
        key: 'add-user',
        label: t(`${TRANSLATION_ROOT}.add-user`),
        onClick: () => setIsUserModalOpen(true),
      },
      {
        key: 'import-users',
        label: t(`${TRANSLATION_ROOT}.import-users`),
        onClick: () => setIsImportUsersModalOpen(true),
      },
    ],
    [t],
  );

  if (isLoading) {
    return null;
  }

  const adminDeleteDisabled = users.filter(isAdmin).length < 2;

  return (
    <div>
      {contextHolder}
      <ImprUserList
        adminDeleteDisabled={adminDeleteDisabled}
        dataSource={users}
        rowKey={'userId'}
        rowSelection={{ selectedRowKeys, onChange: setSelectedRowKeys }}
        customEmptyListNode={
          <IdentityUsersEmpty
            directories={directories}
            refetchDirectories={refetchDirectories}
            refetchUsers={refetchUserList}
          />
        }
        headerExtraComponents={[
          <div key={'invite-user'}>
            {selectedRowKeys.length > 0 && (
              <Button
                size="large"
                aria-label="button-invite-user"
                icon={<SendOutlined />}
                onClick={() => inviteUsers(selectedRowKeys)}
                label={t(`${TRANSLATION_ROOT}.invite-user`)}
                loading={isInvitePending}
              />
            )}
          </div>,
          !!directories?.[0] && <SelectUsers key="add-user" options={selectUsersOptions} />,
        ]}
        headerExtraComponentsLeft={[
          <div key={'select-all'} className="select-all-container">
            {!!users.length && (
              <Checkbox
                indeterminate={selectedRowKeys.length > 0 && selectedRowKeys.length < users.length}
                checked={selectedRowKeys.length === users.length}
                onChange={e => {
                  const allKeys = users.map(u => u.userId as Key);
                  setSelectedRowKeys(e.target.checked ? allKeys : []);
                }}
              />
            )}
          </div>,
        ]}
        enableEdit
        editUser={editUser}
        deleteUser={(userId: string) => {
          setDeletingUserId(userId);
        }}
        enableUnlock={showUnlockUserButton}
        onInviteUser={(id: string) => {
          inviteUsers([id]);
        }}
        unlockUser={(userId: string, hasAccountLock: boolean, factorTypes: string[]) =>
          unlockUser(userId, hasAccountLock, factorTypes)
        }
        isPending={isPending}
        isUnlocked={(userId: string) => unlockedUserIds.has(userId)}
      />
      <div ref={lastElementRef} data-testid="user-list-last-element">
        {isFetchingNextPage && (
          <Flex justify={'center'} style={{ padding: 15 }}>
            <Spin data-testid={'loading-indicator'} size="small" />
          </Flex>
        )}
      </div>
      <div key={'userMenu'}>
        {isUserModalOpen && (
          <UserModal
            editUserData={editUserData}
            domain={directories?.[0].domains?.[0].domainName}
            closeModal={() => {
              setEditUserData(undefined);
              setIsUserModalOpen(false);
            }}
            refetchUsers={refetchUserList}
            adminDeleteDisabled={adminDeleteDisabled}
            isCurrentUser={isCurrentUser(editUserData?.username, sessionUserInfo)}
          />
        )}
        {isImportUsersModalOpen && (
          <ImportUsersModal
            open={isImportUsersModalOpen}
            onClose={() => setIsImportUsersModalOpen(false)}
            onImportFinished={refetchUserList}
          />
        )}

        <Modal
          aria-label="user-modal"
          centered={true}
          open={!!deletingUserId}
          onOk={() =>
            deleteUser(
              { userId: deletingUserId || '' },
              {
                onSuccess: () => {
                  setDeletingUserId(null);
                  refetchUsers();
                },
              },
            )
          }
          okText={t(TRANSLATION_REMOVE)}
          onCancel={() => setDeletingUserId(null)}
          cancelText={t('common.do-not-remove')}
          className={'user-row-menu-modal'}
          maskClosable={false}
        >
          <QuestionImg style={{ width: 64, height: 64, color: '#316A8E' }} />
          <p>{t(`${TRANSLATION_ROOT}.remove-user`)}</p>
        </Modal>
      </div>
    </div>
  );
};

export default IdentityUsers;
